"use strict";

(() => {
    class Rent_Calculator {
        /** @var {HTMLElement} */
        #$widget;

        /** @var {HTMLInputElement} */
        #$initialDepositInput;

        /** @var {HTMLInputElement} */
        #$investmentInput;

        /** @var {NodeListOf<HTMLInputElement>} */
        #$investmentPeriodInputs;

        /** @var {HTMLInputElement} */
        #$horizonInput;

        /** @var {HTMLInputElement} */
        #$interestRateInput;

        /** @var {HTMLInputElement} */
        #$withdrawInput;

        /** @var {HTMLInputElement} */
        #$includeInflationInput;

        /** @var {HTMLInputElement} */
        #$inflationInput;


        constructor($widget) {
            this.#$widget = $widget;
        }

        initialize() {
            // Initialize inputs.
            this.#$initialDepositInput    = this.#$widget.querySelector(".dan-input-initial-deposit");
            this.#$investmentInput        = this.#$widget.querySelector(".dan-input-investment");
            this.#$investmentPeriodInputs = this.#$widget.querySelectorAll(".dan-inputs-investment-period input[type='radio']");
            this.#$horizonInput           = this.#$widget.querySelector(".dan-input-horizon");
            this.#$interestRateInput      = this.#$widget.querySelector(".dan-input-interest-rate");
            this.#$withdrawInput          = this.#$widget.querySelector(".dan-input-withdraw");
            this.#$includeInflationInput  = this.#$widget.querySelector(".dan-input-include-inflation");
            this.#$inflationInput         = this.#$widget.querySelector(".dan-input-inflation");

            this.#$initialDepositInput.addEventListener("input", (this.refreshMainChart).bind(this));
            this.#$investmentInput.addEventListener("input", (this.refreshMainChart).bind(this));
            this.#$horizonInput.addEventListener("input", (this.refreshMainChart).bind(this));
            this.#$interestRateInput.addEventListener("input", (this.refreshMainChart).bind(this));
            this.#$withdrawInput.addEventListener("input", (this.refreshMainChart).bind(this));
            this.#$includeInflationInput.addEventListener("change", (this.refreshInflationInputVisibility).bind(this));
            this.#$includeInflationInput.addEventListener("change", (this.refreshMainChart).bind(this));
            this.#$inflationInput.addEventListener("input", (this.refreshMainChart).bind(this));

            for (const $input of this.#$investmentPeriodInputs) {
                $input.addEventListener("change", (this.refreshMainChart).bind(this));
            }

            // Initialize main chart.
            this.#initializeMainChart();
            this.refreshMainChart();

            // Initialize tooltips.
            const $tooltips = this.#$widget.querySelectorAll("[data-tooltip]");

            if ($tooltips.length > 0) {
                // noinspection JSUnresolvedReference
                tippy($tooltips, {
                    content: ($element) => $element.getAttribute("data-tooltip"),
                    allowHTML: true
                });
            }
        }

        #initializeMainChart() {
            const $mainChart    = this.#$widget.querySelector(".dan-main-chart");
            this.#$widget.chart = new Chart($mainChart, {
                type: "line",
                data: {},
                options: {
                    aspectRatio: 1.4,
                    interaction: {
                        mode: "index",
                        intersect: false
                    },
                    scales: {
                        x: {
                            beginAtZero: true
                        },
                        yValue: {
                            type: "linear",
                            position: "left",
                            beginAtZero: true,
                            ticks: {
                                callback: (value) => {
                                    return Dan_Utilities.formatPrice(value) + " Kč";
                                }
                            }
                        },
                        yRent: {
                            type: "linear",
                            position: "right",
                            beginAtZero: true,
                            ticks: {
                                callback: (value) => {
                                    return Dan_Utilities.formatPrice(value) + " Kč";
                                }
                            },
                            grid: {
                                drawOnChartArea: false
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            onClick: null,
                            labels: {
                                usePointStyle: true,
                                pointStyle: "circle",
                                boxWidth: 6,
                                boxHeight: 6
                            }
                        },
                        tooltip: {
                            displayColors: false,
                            callbacks: {
                                title: (contexts) => {
                                    return (contexts[0].dataIndex > 0)
                                        ? contexts[0].dataIndex + ". rok"
                                        : "Dnes";
                                },
                                label: (context) => {
                                    let label;
                                    label = context.dataset.label + ": ";
                                    label += Dan_Utilities.formatPrice(context.raw) + " Kč";

                                    return label;
                                }
                            }
                        }
                    }
                }
            });
        }

        refreshMainChart() {
            const inputs = this.getInputs();
            const data   = [];

            if (
                inputs.investment !== null
                && inputs.investmentPeriod !== null
                && inputs.horizon !== null
                && inputs.interestRate !== null
                && inputs.withdraw !== null
            ) {
                const initialDeposit = inputs.initialDeposit || 0;

                // Zero year.
                const zeroYearRent = this.#calcMonthlyRent(initialDeposit, inputs.withdraw);

                data.push({
                    year: "Dnes",
                    value: initialDeposit,
                    rent: Math.round(zeroYearRent),
                    valuePurchasePower: initialDeposit,
                    rentPurchasePower: Math.round(zeroYearRent)
                });

                // Other years.
                let value              = initialDeposit;
                let valuePurchasePower = initialDeposit;

                const interestCoeff    = (inputs.interestRate / 100 / 12);
                const devaluationCoeff = (inputs.includeInflation && inputs.inflation)
                    ? (inputs.inflation / 100 / 12)
                    : 0;
                const investment   = (inputs.investmentPeriod === "yearly")
                    ? Math.round(inputs.investment / 12)
                    : inputs.investment;

                for (let year = 1; year <= inputs.horizon; year++) {
                    for (let month = 1; month <= 12; month++) {
                        value += investment;
                        value += (value * interestCoeff);

                        valuePurchasePower += investment;
                        valuePurchasePower = (
                            valuePurchasePower
                            + (valuePurchasePower * interestCoeff)
                            - (valuePurchasePower * devaluationCoeff)
                        );
                    }

                    const rent              = this.#calcMonthlyRent(value, inputs.withdraw);
                    const rentPurchasePower = this.#calcMonthlyRent(valuePurchasePower, inputs.withdraw);

                    data.push({
                        year: year.toString(),
                        value: Math.round(value),
                        rent: Math.round(rent),
                        valuePurchasePower: Math.round(valuePurchasePower),
                        rentPurchasePower: Math.round(rentPurchasePower)
                    });
                }
            }

            const datasets = [
                {
                    label: "Naspořená částka",
                    data: data.map((row) => row.value),
                    yAxisID: "yValue",
                    borderColor: "#292929",
                    borderWidth: 2,
                    backgroundColor: "#292929"
                },
                {
                    label: "Měsíční renta",
                    data: data.map((row) => row.rent),
                    yAxisID: "yRent",
                    borderColor: "#DAFB54",
                    borderWidth: 2,
                    backgroundColor: "#DAFB54"
                }
            ];

            if (inputs.includeInflation && inputs.inflation) {
                datasets.push({
                    label: "Kupní síla naspořené částky",
                    data: data.map((row) => row.valuePurchasePower),
                    yAxisID: "yValue",
                    borderColor: "#0766AD",
                    borderWidth: 2,
                    backgroundColor: "#0766AD"
                });

                datasets.push({
                    label: "Kupní síla měsíční renty",
                    data: data.map((row) => row.rentPurchasePower),
                    yAxisID: "yRent",
                    borderColor: "#FF0000",
                    borderWidth: 2,
                    backgroundColor: "#FF0000"
                });
            }

            // noinspection JSUnresolvedReference
            this.#$widget.chart.data = {
                labels: data.map((row) => row.year),
                datasets: datasets
            };

            // noinspection JSUnresolvedReference
            this.#$widget.chart.update("none");
        }

        #calcMonthlyRent(value, withdrawPercent) {
            return (value * (withdrawPercent / 100) / 12);
        }

        refreshInflationInputVisibility() {
            const $inflationWrapper = this.#$widget.querySelector(".dan-inflation-input-wrapper");
            const inflationShown    = $inflationWrapper.classList.contains("-active");

            if (this.#$includeInflationInput.checked && !inflationShown) {
                $inflationWrapper.style.height = "0px";

                setTimeout(() => {
                    $inflationWrapper.style.height = $inflationWrapper.scrollHeight + "px";
                }, 25);

                setTimeout(() => {
                    $inflationWrapper.style.height = "auto";
                    $inflationWrapper.classList.add("-active");
                }, 175);
            } else if (!this.#$includeInflationInput.checked && inflationShown) {
                $inflationWrapper.style.height = $inflationWrapper.scrollHeight + "px";

                setTimeout(() => {
                    $inflationWrapper.style.height = "0px";
                }, 25);

                setTimeout(() => {
                    $inflationWrapper.classList.remove("-active");
                }, 175);
            }
        }

        getInputs() {
            // Initial deposit.
            let initialDeposit;
            initialDeposit = this.#$initialDepositInput.value || this.#$initialDepositInput.placeholder;
            initialDeposit = parseInt(initialDeposit);

            if (isNaN(initialDeposit) || initialDeposit < 0) {
                initialDeposit = null;
            }

            // Investment.
            let investment;
            investment = this.#$investmentInput.value || this.#$investmentInput.placeholder;
            investment = parseInt(investment);

            if (isNaN(investment) || investment < 0) {
                investment = null;
            }

            // Investment period.
            let investmentPeriod = null;

            for (const $input of this.#$investmentPeriodInputs) {
                if ($input.checked) {
                    investmentPeriod = $input.value;
                    break;
                }
            }

            // Horizon.
            let horizon;
            horizon = this.#$horizonInput.value || this.#$horizonInput.placeholder;
            horizon = parseInt(horizon);

            if (isNaN(horizon) || horizon < 1) {
                horizon = null;
            }

            // Inretest rate.
            let rate;
            rate = this.#$interestRateInput.value || this.#$interestRateInput.placeholder;
            rate = parseFloat(rate);

            if (!isNaN(rate) && rate >= 0) {
                rate = (Math.round(rate * 100) / 100);
            } else {
                rate = null;
            }

            // Withdraw.
            let withdraw;
            withdraw = this.#$withdrawInput.value || this.#$withdrawInput.placeholder;
            withdraw = parseFloat(withdraw);

            if (!isNaN(withdraw) && withdraw >= 0.1 && withdraw <= 100) {
                withdraw = (Math.round(withdraw * 10) / 10);
            } else {
                withdraw = null;
            }

            // Inflation.
            const includeInflation = this.#$includeInflationInput.checked;

            let inflation;
            inflation = this.#$inflationInput.value || this.#$inflationInput.placeholder;
            inflation = parseFloat(inflation);

            if (includeInflation && !isNaN(inflation) && inflation >= 0) {
                inflation = (Math.round(inflation * 10) / 10);
            } else {
                inflation = null;
            }

            // Return.
            return {
                initialDeposit: initialDeposit,
                investment: investment,
                investmentPeriod: investmentPeriod,
                horizon: horizon,
                interestRate: rate,
                withdraw: withdraw,
                includeInflation: includeInflation,
                inflation: inflation
            };
        }
    }

    // Initialize widgets.
    const $widgets = document.querySelectorAll(".dan-rent-calculator");

    for (const $widget of $widgets) {
        $widget.calculator = new Rent_Calculator($widget);
        $widget.calculator.initialize();
    }
})();
